<template>
  <a-layout class="main-layout">
    <!-- 侧边栏 -->
    <a-layout-sider
      v-model:collapsed="collapsed"
      :trigger="null"
      collapsible
      theme="dark"
      width="220"
    >
      <div class="logo">
        <span v-if="!collapsed">资质365智能预警系统</span>
        <span v-else>资质365</span>
      </div>
      
      <a-menu
        v-model:selectedKeys="selectedKeys"
        v-model:openKeys="openKeys"
        theme="dark"
        mode="inline"
        @click="handleMenuClick"
      >
        <!-- 动态渲染菜单 -->
        <template v-for="menu in displayMenus" :key="menu.key">
          <!-- 一级菜单（无子菜单）-->
          <a-menu-item v-if="!menu.children || menu.children.length === 0" :key="menu.key">
            <template #icon>
              <component :is="getMenuIcon(menu.icon)" />
            </template>
            <span>{{ menu.title }}</span>
          </a-menu-item>
          
          <!-- 子菜单（有子菜单）-->
          <a-sub-menu v-if="menu.children && menu.children.length > 0" :key="menu.key">
            <template #title>
              <component :is="getMenuIcon(menu.icon)" />
              <span>{{ menu.title }}</span>
            </template>
            <a-menu-item v-for="child in menu.children" :key="child.key">
              <span>{{ child.title }}</span>
            </a-menu-item>
          </a-sub-menu>
        </template>
      </a-menu>
    </a-layout-sider>
    
    <!-- 主内容区 -->
    <a-layout>
      <!-- 顶部栏 -->
      <a-layout-header class="header">
        <div class="header-left">
          <menu-unfold-outlined
            v-if="collapsed"
            class="trigger"
            @click="collapsed = !collapsed"
          />
          <menu-fold-outlined
            v-else
            class="trigger"
            @click="collapsed = !collapsed"
          />
          
          <!-- 全局搜索按钮 -->
          <!-- <a-button type="text" class="search-btn" @click="openSearch">
            <template #icon>
              <SearchOutlined />
            </template>
            <span class="search-text">搜索...</span>
            <span class="search-shortcut">Ctrl+K</span>
          </a-button> -->
        </div>
        
        <div class="header-right">
          <a-space :size="20">
            <!-- 主题切换 -->
            <a-tooltip title="切换主题">
              <BulbOutlined 
                class="header-icon" 
                @click="toggleTheme"
                :style="{ color: isDark ? '#ffd700' : '#666' }"
              />
            </a-tooltip>
            
            <!-- 消息中心 -->
            <a-badge :count="unreadCount" :overflow-count="99">
              <BellOutlined class="header-icon" @click="handleGoMessage" />
            </a-badge>
            
            <a-dropdown>
              <div class="user-info">
                <a-avatar :size="32">
                  <template #icon>
                    <UserOutlined />
                  </template>
                </a-avatar>
                <span class="username">{{ userStore.username }}</span>
              </div>
              <template #overlay>
                <a-menu style="width: 110px;">
                  <a-menu-item key="profile" @click="handleGoProfile">
                    <UserOutlined />
                    <span>个人中心</span>
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item key="logout" @click="handleLogout">
                    <LogoutOutlined />
                    <span>退出登录</span>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </a-space>
        </div>
      </a-layout-header>
      
      <!-- 内容区 -->
       <a-watermark :content="[userStore.username]">
        <a-layout-content class="content">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </a-layout-content>
      </a-watermark>
      
      <!-- 底部 -->
      <a-layout-footer class="footer">
        华创建研 ©2026
      </a-layout-footer>
    </a-layout>
    
    <!-- 全局搜索组件 -->
    <GlobalSearch ref="globalSearchRef" />
  </a-layout>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  CloudOutlined,
  AuditOutlined,
  ApiOutlined,
  BgColorsOutlined,
  // SearchOutlined,
  BulbOutlined,
  DingdingOutlined,
} from '@ant-design/icons-vue';
import { useUserStore } from '@/store/modules/user';
import { useMenuStore } from '@/store/modules/menu';
import { usePermissionStore } from '@/store/modules/permission';

import { getMessageStats } from '@/api/message';
import GlobalSearch from '@/components/GlobalSearch.vue';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const menuStore = useMenuStore();
const permissionStore = usePermissionStore();

// 全局搜索组件引用
const globalSearchRef = ref(null);

// 主题状态
const isDark = ref(false);

// 未读消息数
const unreadCount = ref(0);
let messageTimer = null;

// 从 localStorage 恢复菜单展开/收起状态
const getInitialCollapsed = () => {
  const stored = localStorage.getItem('sidebarCollapsed');
  return stored ? JSON.parse(stored) : false;
};

// 从 localStorage 恢复菜单展开状态
const getInitialOpenKeys = () => {
  const stored = localStorage.getItem('menuOpenKeys');
  return stored ? JSON.parse(stored) : [];
};

const collapsed = ref(getInitialCollapsed());
const selectedKeys = ref([route.path]);
const openKeys = ref(getInitialOpenKeys());

// 监听菜单展开/收起状态变化，保存到 localStorage
watch(
  () => collapsed.value,
  (newValue) => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newValue));
  }
);

// 监听 openKeys 变化，保存到 localStorage
watch(
  () => openKeys.value,
  (newValue) => {
    localStorage.setItem('menuOpenKeys', JSON.stringify(newValue));
  },
  { deep: true }
);

// 基础菜单（固定菜单，始终显示）
const baseMenus = [
  {
    key: '/dashboard',
    title: '首页',
    icon: 'DashboardOutlined',
    path: '/dashboard',
  },
  // 超链接 点击新开页面
  {
    key: '/bigscreen',
    title: '智慧大屏',
    icon: 'DashboardOutlined',
    path: '/bigscreen',
    target: '_blank',
    meta: { requiresAuth: true, title: '智慧大屏', icon: 'DashboardOutlined', link: '/bigscreen', target: '_blank' },
  }
];

// 图标映射
const iconMap = {
  DashboardOutlined,
  SettingOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  CloudOutlined,
  AuditOutlined,
  ApiOutlined,
  BgColorsOutlined,
  DingdingOutlined,
};

// 菜单图标不存在时，使用默认图标
const getMenuIcon = (iconName) => {
  return iconMap[iconName] || DatabaseOutlined;
};

// 合并及排序菜单数据
const displayMenus = computed(() => {
  const backendMenus = menuStore.menus || [];
  
  // 后端已经返回树形结构，直接使用
  // 将后端菜单数据转换为前端格式
  const menuTree = convertBackendMenus(backendMenus);

  // console.log(111,menuTree)
  
  // 合并基础菜单和后端菜单，去掉重覆
  const allMenus = [...baseMenus];
  const existingKeys = new Set(allMenus.map(m => m.key));
  
  menuTree.forEach(menu => {
    if (!existingKeys.has(menu.key)) {
      allMenus.push(menu);
    }
  });
  
  // 按 sort_order 排序
  return allMenus.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
});

/**
 * 转换后端菜单数据为前端格式
 * 后端已经返回树形结构，只需要转换字段格式
 * @param menus - 后端菜单树
 * @returns - 前端格式菜单树
 */
const convertBackendMenus = (menus) => {
  if (!Array.isArray(menus)) return [];
  
  return menus.map(menu => ({
    key: menu.path || `/menu-${menu.id}`,
    title: menu.title,
    icon: menu.icon,
    path: menu.path,
    sort_order: menu.sort_order,
    // 递归转换子菜单
    children: menu.children && menu.children.length > 0 
      ? menu.children.map(child => ({
          key: child.path || `/menu-${child.id}`,
          title: child.title,
          icon: child.icon,
          path: child.path,
          sort_order: child.sort_order,
        }))
      : [],
  }));
};

/**
 * 根据当前路由路径，自动展开对应的父级菜单
 * @param path - 当前路由路径
 * @param menus - 菜单列表
 */
const updateOpenKeysByPath = (path, menus) => {
  const parentMenu = menus.find(m => 
    m.children && m.children.some(c => c.path === path)
  );
  
  if (parentMenu) {
    openKeys.value = [parentMenu.key];
  } else {
    // 如果当前路径是一级菜单，清空 openKeys
    const isTopLevelMenu = menus.some(m => m.path === path);
    if (!isTopLevelMenu) {
      // 保持之前的展开状态（从 localStorage 恢复）
      openKeys.value = getInitialOpenKeys();
    }
  }
};

// 加载未读消息数
const loadUnreadCount = async () => {
  try {
    const res = await getMessageStats();
    if (res.code === 200) {
      unreadCount.value = res.data.unread || 0;
    }
  } catch (error) {
    console.error('加载未读消息数失败:', error);
  }
};

// 加载菜单数据
onMounted(async () => {
  // 初始化主题
  initTheme();
  
  // 绑定快捷键
  window.addEventListener('keydown', handleKeydown);
  
  // 优先使用缓存的菜单数据
  const cached = menuStore.getMenusFromCache();
  console.log('[MainLayout] 使用缓存的菜单数据', cached);
  if (cached && cached.length > 0) {
    // 存在缓存，使用缓存的菜单
    menuStore.menus = cached;
  } else {
    // 无缓存，从后端加载菜单
    console.log('[MainLayout] 无缓存，从后端加载菜单');
    try {
      await menuStore.fetchMenus();
    } catch (error) {
      console.error('加载菜单失败:', error);
    }
  }

  const cachedPermissions = permissionStore.getPermissionsFromCache();
  if (cachedPermissions && cachedPermissions.length > 0) {
    // 存在缓存，使用缓存的权限
    permissionStore.permissions = cachedPermissions;
  } else {
    // 无缓存，从后端加载权限
    try {
      await permissionStore.fetchPermissions();
    } catch (error) {
      console.error('加载权限失败:', error);
    }
  }
  
  // 菜单加载完成后，根据当前路由自动展开对应的父级菜单
  updateOpenKeysByPath(route.path, displayMenus.value);
  
  // 加载未读消息数
  loadUnreadCount();
  
  // 每30秒刷新一次未读消息数
  messageTimer = setInterval(loadUnreadCount, 30000);
});

// 组件卸载时清除定时器
onUnmounted(() => {
  if (messageTimer) {
    clearInterval(messageTimer);
  }
  // 移除快捷键监听
  window.removeEventListener('keydown', handleKeydown);
});

// 监听路由变化，更新选中的菜单
watch(
  () => route.path,
  (newPath) => {
    selectedKeys.value = [newPath];
    // 自动展开对应的父级菜单
    updateOpenKeysByPath(newPath, displayMenus.value);
  }
);

// 菜单点击
const handleMenuClick = ({ key }) => {
  // 需要检查key是否为子指令（包含/）
  if (key.includes('/')) {
    router.push(key).catch(err => {
      console.error('[MainLayout] 路由跳转失败:', err);
    });
  }
};

// 跳转消息中心
const handleGoMessage = () => {
  router.push('/message-center').catch(err => {
    console.error('[MainLayout] 路由跳转失败:', err);
  });
};

// 个人中心
const handleGoProfile = () => {
  router.push('/profile').catch(err => {
    console.error('[MainLayout] 路由跳转失败:', err);
  });
};

// 退出登录
const handleLogout = () => {
  // 清除菜单数据
  menuStore.clearMenus();
  permissionStore.clearPermissions();
  // 清除用户信息
  userStore.logout();
  message.success('已退出登录');
  router.push('/login');
};

// 打开全局搜索
const openSearch = () => {
  globalSearchRef.value?.open();
};

// 主题切换
const toggleTheme = () => {
  isDark.value = !isDark.value;
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light');
  
  // 切换 body 的 class
  if (isDark.value) {
    document.body.classList.add('dark-theme');
  } else {
    document.body.classList.remove('dark-theme');
  }
  
  message.success(`已切换到${isDark.value ? '暗色' : '亮色'}主题`);
};

// 初始化主题
const initTheme = () => {
  const savedTheme = localStorage.getItem('theme');
  isDark.value = savedTheme === 'dark';
  
  if (isDark.value) {
    document.body.classList.add('dark-theme');
  }
};

// 监听快捷键
const handleKeydown = (e) => {
  // Ctrl+K 或 Cmd+K 打开搜索
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault();
    openSearch();
  }
};
</script>

<style scoped>
.main-layout {
  min-height: 100vh;
}

.logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.1);
}

.header {
  background: white;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-btn {
  margin-left: 20px;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.search-btn:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}

.search-text {
  color: #999;
  font-size: 14px;
}

.search-shortcut {
  font-size: 12px;
  color: #bbb;
  padding: 2px 6px;
  background: #f5f5f5;
  border-radius: 3px;
  border: 1px solid #e8e8e8;
}

.trigger {
  font-size: 18px;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

.header-right {
  display: flex;
  align-items: center;
}

.header-icon {
  font-size: 18px;
  cursor: pointer;
  transition: color 0.3s;
}

.header-icon:hover {
  color: #1890ff;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 10px;
}

.username {
  font-size: 14px;
}

.content {
  margin: 24px;
  /* padding: 24px; */
  background: white;
  min-height: calc(100vh - 64px - 70px - 48px);
  border-radius: 4px;
}

.footer {
  text-align: center;
  background: #f0f2f5;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* 暗色主题全局样式 - 优化版 */
body.dark-theme {
  background: #0d1117;
  color: #e6edf3;
}

/* Header 顶部栏 */
body.dark-theme .header {
  background: #161b22;
  border-bottom: 1px solid #30363d;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* Content 内容区 */
body.dark-theme .content {
  background: #0d1117;
  color: #e6edf3;
}

/* Layout 布局背景 */
body.dark-theme .ant-layout {
  background: #0d1117;
}

/* Footer 底部 */
body.dark-theme .footer {
  background: #0d1117;
  color: #7d8590;
  border-top: 1px solid #21262d;
}

/* 搜索按钮 */
body.dark-theme .search-btn {
  background: #21262d;
  border-color: #30363d;
  color: #e6edf3;
}

body.dark-theme .search-btn:hover {
  background: #30363d;
  border-color: #58a6ff;
}

body.dark-theme .search-text {
  color: #8b949e;
}

body.dark-theme .search-shortcut {
  background: #161b22;
  border-color: #30363d;
  color: #8b949e;
}

/* 触发器图标 */
body.dark-theme .trigger {
  color: #e6edf3;
}

body.dark-theme .trigger:hover {
  color: #58a6ff;
  background: rgba(88, 166, 255, 0.1);
}

/* 用户名 */
body.dark-theme .username {
  color: #e6edf3;
}

/* 头部图标 */
body.dark-theme .header-icon {
  color: #e6edf3;
  transition: all 0.3s ease;
}

body.dark-theme .header-icon:hover {
  color: #58a6ff;
  background: rgba(88, 166, 255, 0.1);
  border-radius: 4px;
}

/* Card 卡片 - 提升对比度 */
body.dark-theme .ant-card {
  background: #161b22;
  border: 1px solid #30363d;
  color: #e6edf3;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

body.dark-theme .ant-card-head {
  background: #161b22;
  border-bottom: 1px solid #30363d;
  color: #e6edf3;
}

body.dark-theme .ant-card-head-title {
  color: #e6edf3;
  font-weight: 600;
}

body.dark-theme .ant-card-body {
  color: #e6edf3;
}

/* 统计数字 */
body.dark-theme .ant-statistic-title {
  color: #8b949e;
  font-size: 14px;
}

body.dark-theme .ant-statistic-content {
  color: #e6edf3;
  font-weight: 600;
}

/* Table 表格 - 增强对比度 */
body.dark-theme .ant-table {
  background: #161b22;
  color: #e6edf3;
}

body.dark-theme .ant-table-wrapper {
  background: #161b22;
}

body.dark-theme .ant-table-container {
  border-color: #30363d;
}

body.dark-theme .ant-table-thead > tr > th {
  background: #21262d;
  border-color: #30363d;
  color: #e6edf3;
  font-weight: 600;
}

body.dark-theme .ant-table-tbody > tr > td {
  border-color: #21262d;
  color: #e6edf3;
  background: #161b22;
}

body.dark-theme .ant-table-tbody > tr:hover > td {
  background: #21262d !important;
}

body.dark-theme .ant-table-tbody > tr.ant-table-row-selected > td {
  background: #1c2128;
  border-color: #30363d;
}

body.dark-theme .ant-table-placeholder {
  background: #161b22;
  color: #8b949e;
}

/* Pagination 分页 */
body.dark-theme .ant-pagination-item {
  background: #161b22;
  border-color: #30363d;
}

body.dark-theme .ant-pagination-item a {
  color: #e6edf3;
}

body.dark-theme .ant-pagination-item-active {
  background: #1f6feb;
  border-color: #1f6feb;
}

body.dark-theme .ant-pagination-item-active a {
  color: #ffffff;
}

/* Input 输入框 */
body.dark-theme .ant-input,
body.dark-theme .ant-input-number,
body.dark-theme .ant-input-number-input,
body.dark-theme .ant-select-selector,
body.dark-theme .ant-picker {
  background: #0d1117;
  border-color: #30363d;
  color: #e6edf3;
}

body.dark-theme .ant-input:hover,
body.dark-theme .ant-select-selector:hover,
body.dark-theme .ant-picker:hover {
  border-color: #58a6ff;
}

body.dark-theme .ant-input:focus,
body.dark-theme .ant-select-focused .ant-select-selector {
  border-color: #58a6ff;
  box-shadow: 0 0 0 2px rgba(88, 166, 255, 0.1);
}

body.dark-theme .ant-input::placeholder {
  color: #6e7681;
}

/* Select 下拉选择 */
body.dark-theme .ant-select-dropdown {
  background: #161b22;
  border-color: #30363d;
}

body.dark-theme .ant-select-item {
  color: #e6edf3;
}

body.dark-theme .ant-select-item-option-selected {
  background: #21262d;
  color: #58a6ff;
}

body.dark-theme .ant-select-item-option-active {
  background: #21262d;
}

/* Button 按钮 */
body.dark-theme .ant-btn-default {
  background: #21262d;
  border-color: #30363d;
  color: #e6edf3;
}

body.dark-theme .ant-btn-default:hover {
  background: #30363d;
  border-color: #58a6ff;
  color: #58a6ff;
}

body.dark-theme .ant-btn-primary {
  background: #1f6feb;
  border-color: #1f6feb;
  color: #ffffff;
}

body.dark-theme .ant-btn-primary:hover {
  background: #388bfd;
  border-color: #388bfd;
}

/* Modal 模态框 */
body.dark-theme .ant-modal-content {
  background: #161b22;
  color: #e6edf3;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

body.dark-theme .ant-modal-header {
  background: #161b22;
  border-bottom: 1px solid #30363d;
  color: #e6edf3;
}

body.dark-theme .ant-modal-title {
  color: #e6edf3;
  font-weight: 600;
}

body.dark-theme .ant-modal-footer {
  background: #161b22;
  border-top: 1px solid #30363d;
}

body.dark-theme .ant-modal-close {
  color: #8b949e;
}

body.dark-theme .ant-modal-close:hover {
  color: #e6edf3;
}

/* Tabs 标签页 */
body.dark-theme .ant-tabs-nav {
  background: #161b22;
}

body.dark-theme .ant-tabs-tab {
  color: #8b949e;
}

body.dark-theme .ant-tabs-tab:hover {
  color: #e6edf3;
}

body.dark-theme .ant-tabs-tab-active .ant-tabs-tab-btn {
  color: #e6edf3;
}

body.dark-theme .ant-tabs-ink-bar {
  background: #58a6ff;
}

/* Badge 徽章 */
body.dark-theme .ant-badge-count {
  background: #f85149;
}

/* Tag 标签 */
body.dark-theme .ant-tag {
  background: #21262d;
  border-color: #30363d;
  color: #e6edf3;
}

/* Divider 分割线 */
body.dark-theme .ant-divider {
  border-color: #21262d;
}

/* Dropdown 下拉菜单 */
body.dark-theme .ant-dropdown-menu {
  background: #161b22;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

body.dark-theme .ant-dropdown-menu-item {
  color: #e6edf3;
}

body.dark-theme .ant-dropdown-menu-item:hover {
  background: #21262d;
}

/* List 列表 */
body.dark-theme .ant-list {
  color: #e6edf3;
}

body.dark-theme .ant-list-item {
  border-color: #21262d;
}

body.dark-theme .ant-list-item:hover {
  background: #161b22;
}

/* Empty 空状态 */
body.dark-theme .ant-empty-description {
  color: #8b949e;
}

/* Form 表单 */
body.dark-theme .ant-form-item-label > label {
  color: #e6edf3;
}

body.dark-theme .ant-form-item-explain-error {
  color: #f85149;
}

/* Switch 开关 */
body.dark-theme .ant-switch {
  background: #30363d;
}

body.dark-theme .ant-switch-checked {
  background: #1f6feb;
}

/* Progress 进度条 */
body.dark-theme .ant-progress-text {
  color: #e6edf3;
}
</style>
